// The firing phase: a battery shooting in sequence.
//
// **Its own clock, not a pass of `CargoMotion`.** That queue is four passes
// over the *ingots*, each one beat long and played strictly one after another,
// which is right for a pipeline where every stage waits on the one before it.
// Guns do not wait on each other. They fire in sequence for legibility — you
// need to see which one shot and what it hit — and a fifth gun is not four
// beats behind the first, it is one second behind it, still firing while the
// first is still glowing.
//
// So the model is a **stagger with overlap**: shot `i` starts at `i × STAGGER`
// and each beam lasts `BEAM` regardless, which is longer than the stagger. Two
// consequences fall out rather than being arranged — a lone turret fires
// immediately, and a row of eight reads as a ripple rather than as a volley.
//
// Pure, and tested apart from the scene, for the reason `descent.ts` is: this
// plays for about a second a turn and nobody would catch a regression in it by
// looking.

/** One turret shot, as the tick reports it (`TickFacts.shots`). */
export interface TurretShot {
  from: number;
  to: number;
  killed: boolean;
}

/**
 * How long between one gun firing and the next.
 *
 * A quarter second is about the shortest gap two events can have and still
 * read as two events. Below it a battery is one flash.
 */
export const STAGGER = 0.25;

/**
 * How long a beam is drawn.
 *
 * **Longer than the stagger on purpose**, which is the whole of what makes
 * this overlap: at or under it the guns would take strict turns and eight of
 * them would be two seconds of the player watching.
 */
export const BEAM = 0.4;

/** A beam on screen right now. */
export interface Beam {
  from: number;
  to: number;
  /** 0 at the moment it fires, 1 as it fades out. */
  through: number;
  /** Whether the shot killed, so the hit can be drawn differently. */
  killed: boolean;
}

/**
 * A battery firing.
 *
 * Fed the shots the rules reported (`TickReport.shots`) — the same split
 * `CargoMove` makes, where the rules say what happened and the renderer says
 * where it was on screen. A count could not be drawn: it says something died
 * and nothing about where the guns were pointing.
 */
export class TurretFire {
  private shots: TurretShot[] = [];
  private clock = 0;
  /** Which shots have already been announced, so a gun is aimed exactly once. */
  private fired = 0;

  /**
   * Begin a volley. An empty one leaves the model idle rather than running for
   * a beat with nothing in it — the render loop is on-demand, and a board with
   * no turrets on it must not wake it every turn.
   */
  start(shots: readonly TurretShot[]): void {
    this.shots = [...shots];
    this.clock = 0;
    this.fired = 0;
  }

  /** True while any beam is still on screen. */
  get running(): boolean {
    return this.shots.length > 0 && this.clock < this.endsAt;
  }

  /** When the last beam finishes. */
  private get endsAt(): number {
    return (this.shots.length - 1) * STAGGER + BEAM;
  }

  /**
   * Advance the volley, and report every gun that has *just* opened fire.
   *
   * Returning the new shots rather than having the caller diff them is what
   * keeps "aim the gun" a single event: a turret is trained on its target at
   * the instant it fires and holds that bearing until the battery stands down,
   * so an aim applied every frame would be the same instruction repeated and an
   * aim applied on a diff would need this model's state kept somewhere else.
   */
  advance(dt: number): TurretShot[] {
    if (!this.running) return [];
    this.clock += dt;

    const opened: TurretShot[] = [];
    while (this.fired < this.shots.length && this.clock >= this.fired * STAGGER) {
      opened.push(this.shots[this.fired]);
      this.fired++;
    }
    return opened;
  }

  /** Every beam currently drawn, oldest first. */
  beams(): Beam[] {
    const out: Beam[] = [];
    for (let i = 0; i < this.shots.length; i++) {
      const since = this.clock - i * STAGGER;
      if (since < 0 || since > BEAM) continue;
      out.push({
        from: this.shots[i].from,
        to: this.shots[i].to,
        through: since / BEAM,
        killed: this.shots[i].killed
      });
    }
    return out;
  }

  /** True once every gun has fired and every beam has faded. */
  get done(): boolean {
    return this.shots.length > 0 && this.clock >= this.endsAt;
  }

  /** Forget the volley, so `done` cannot fire twice for one battery. */
  clear(): void {
    this.shots = [];
    this.clock = 0;
    this.fired = 0;
  }
}
